import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { convertUtcToIstWithTime } from "../../utilities";

const STATUS_COLORS = {
  SUCCESS:   "bg-green-100 text-green-700 border-green-200",
  INITIATED: "bg-yellow-100 text-yellow-700 border-yellow-200",
  FAILED:    "bg-red-100 text-red-700 border-red-200",
};

const PACK_LABELS = {
  single_unlock: "Single Unlock",
  spark_bundle:  "Spark Bundle",
  premium:       "Premium",
  elite:         "Elite",
  annual:        "Annual",
};

const PACK_ICONS = {
  single_unlock: "🔓",
  spark_bundle:  "✨",
  premium:       "💎",
  elite:         "👑",
  annual:        "🏆",
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start py-3 border-b border-gray-100 last:border-0">
    <dt className="w-44 text-xs font-semibold text-gray-500 uppercase tracking-wide pt-0.5 flex-shrink-0">
      {label}
    </dt>
    <dd className={`flex-1 text-sm text-gray-900 ${mono ? "font-mono" : "font-medium"}`}>
      {value ?? "—"}
    </dd>
  </div>
);

const PackPurchaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await func.get(`${urls.GET_PACK_PURCHASE_DETAIL_URL}${id}`);
        if (res.status === 200) setOrder(res.data.data);
        else setError("Transaction not found.");
      } catch {
        setError("Failed to load transaction.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-400">Loading transaction…</div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <button onClick={() => navigate(-1)} className="text-blue-600 text-sm hover:underline mb-4 flex items-center gap-1">
          ← Back
        </button>
        <div className="text-red-500 font-medium">{error ?? "Not found"}</div>
      </div>
    );
  }

  const statusClass = STATUS_COLORS[order.payment_status] ?? "bg-gray-100 text-gray-600 border-gray-200";
  const discount = order.originalAmount && order.amount
    ? order.originalAmount - order.amount
    : 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 text-sm hover:underline mb-5 flex items-center gap-1"
      >
        ← Back to Pack Purchases
      </button>

      {/* Header card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-5">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1">
                Transaction Receipt
              </p>
              <h2 className="text-2xl font-bold font-mono">
                {order.readableTransactionId ?? "—"}
              </h2>
            </div>
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${statusClass}`}
            >
              {order.payment_status}
            </span>
          </div>
        </div>

        {/* Amount hero */}
        <div className="px-6 py-5 flex items-center gap-6 border-b border-gray-100">
          <div className="flex-1">
            <p className="text-xs text-gray-500 mb-1">Amount Paid</p>
            <p className="text-3xl font-bold text-gray-900">
              ₹{(order.amount ?? 0).toLocaleString("en-IN")}
            </p>
            {discount > 0 && (
              <p className="text-xs text-green-600 mt-1 font-semibold">
                Saved ₹{discount.toLocaleString("en-IN")} with promo code
              </p>
            )}
          </div>
          <div className="text-5xl">{PACK_ICONS[order.packType] ?? "📦"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Transaction Details */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Transaction Details</h3>
          <dl>
            <Row label="Transaction ID"    value={order.readableTransactionId} mono />
            <Row label="Pack"              value={`${PACK_ICONS[order.packType] ?? ""} ${PACK_LABELS[order.packType] ?? order.packType}`} />
            <Row label="Original Amount"   value={order.originalAmount ? `₹${order.originalAmount.toLocaleString("en-IN")}` : "—"} />
            {discount > 0 && (
              <Row label="Promo Discount" value={`-₹${discount.toLocaleString("en-IN")} (${order.promoCode})`} />
            )}
            <Row label="Final Amount"      value={`₹${(order.amount ?? 0).toLocaleString("en-IN")}`} />
            <Row label="Status"            value={order.payment_status} />
            <Row label="Date & Time"       value={order.createdAt ? convertUtcToIstWithTime(order.createdAt) : "—"} />
            <Row label="PG Ref"            value={order.payment_transaction_id} mono />
            <Row label="Internal Ref"      value={order.merchant_transaction_id} mono />
          </dl>
        </div>

        {/* User Details */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">User Details</h3>
          <dl>
            <Row label="Name"  value={order.seeker?.name} />
            <Row label="Phone" value={order.user?.phone} />
            <Row label="Email" value={order.seeker?.email ?? order.user?.email} />
          </dl>

          {order.seeker?.userId && (
            <button
              onClick={() =>
                navigate(`/user-management/match-seekers/view/${order.userId}`)
              }
              className="mt-5 w-full py-2.5 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold rounded-lg hover:bg-blue-100 transition"
            >
              View User Profile →
            </button>
          )}
        </div>
      </div>

      {/* Raw PG response (collapsed) */}
      {order.pg_response && (
        <details className="mt-5 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          <summary className="px-5 py-3 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100">
            Raw Gateway Response
          </summary>
          <pre className="px-5 py-4 text-xs text-gray-700 overflow-x-auto max-h-64 overflow-y-auto">
            {JSON.stringify(order.pg_response, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default PackPurchaseDetail;
